import { useState } from 'react'
import { Bookmark, Trash2, ExternalLink, Calendar, Tag, ImageIcon, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { newsApi } from '@/services/api'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { motion } from 'framer-motion'
import { formatDate } from '@/lib/utils'
import { getGradientForId } from '@/lib/config'

export function DashboardBookmarks() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [articleToDelete, setArticleToDelete] = useState(null)

  // Fetch bookmarks
  const { data: bookmarksData, isLoading, error } = useQuery({
    queryKey: ['bookmarks', page],
    queryFn: async () => {
      const response = await newsApi.getBookmarks(page)
      return response.data
    },
    onError: () => {
      toast.error('Failed to load bookmarks')
    }
  })

  // Remove bookmark mutation
  const removeBookmarkMutation = useMutation({
    mutationFn: (slug) => newsApi.toggleBookmark(slug),
    onSuccess: () => {
      queryClient.invalidateQueries(['bookmarks'])
      toast.success('Bookmark removed')
    },
    onError: () => {
      toast.error('Failed to remove bookmark')
    }
  })

  const bookmarks = bookmarksData?.results || []
  const hasNextPage = bookmarksData?.next
  const hasPreviousPage = bookmarksData?.previous

  const handleRemoveBookmark = (slug) => {
    removeBookmarkMutation.mutate(slug)
    setDeleteConfirmOpen(false)
    setArticleToDelete(null)
  }

  const openDeleteConfirm = (article) => {
    setArticleToDelete(article)
    setDeleteConfirmOpen(true)
  }

  if (isLoading) {
    return (
      <div className="w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Bookmark className="w-8 h-8 text-primary" />
            Bookmarks
          </h1>
          <p className="text-gray-600">Manage your saved articles</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Bookmark className="w-8 h-8 text-primary" />
          Bookmarks
        </h1>
        <p className="text-gray-600">
          {bookmarks.length > 0 
            ? `You have ${bookmarksData?.count || bookmarks.length} saved articles`
            : 'Manage your saved articles'
          }
        </p>
      </div>

      {bookmarks.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
          <Bookmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No bookmarks yet
          </h3>
          <p className="text-gray-600 mb-6">
            Start bookmarking articles to save them for later reading
          </p>
          <Button onClick={() => window.location.href = '/'}>
            Browse Articles
          </Button>
        </div>
      ) : (
        <>
          {/* Bookmarks Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {bookmarks.map((article, index) => (
              <motion.div
                key={article.alias}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Article Image */}
                <div className="relative h-48 overflow-hidden">
                  {article.resized_featured_image || article.thumbnail ? (
                    <img
                      src={article.resized_featured_image || article.thumbnail}
                      alt={article.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center"
                      style={{ background: getGradientForId(article.alias || article.slug) }}
                    >
                      <ImageIcon className="w-16 h-16 text-white/80" />
                    </div>
                  )}
                </div>

                <div className="p-6">
                  {/* Title */}
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                    {article.title}
                  </h3>

                  {/* Excerpt */}
                  {article.excerpt && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>
                  )}

                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                    {article.pub_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(article.pub_date)}
                      </span>
                    )}
                    {article.feeder?.display_name && (
                      <span className="truncate">
                        {article.feeder.display_name}
                      </span>
                    )}
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4 min-h-[28px]">
                    {article.tags && article.tags.length > 0 ? (
                      <>
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag.alias}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                          >
                            <Tag className="w-3 h-3" />
                            {tag.title}
                          </span>
                        ))}
                        {article.tags.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{article.tags.length - 3} more
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 text-gray-400 rounded text-xs">
                        <Tag className="w-3 h-3" />
                        No tags
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => window.location.href = `/posts/${article.slug}/`}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Read
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDeleteConfirm(article)}
                      disabled={removeBookmarkMutation.isLoading}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {(hasNextPage || hasPreviousPage) && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 p-4 bg-white rounded-lg border border-gray-200">
              <div className="text-sm text-gray-600">
                {bookmarksData?.count && (
                  <span>
                    Showing {((page - 1) * 10) + 1} - {Math.min(page * 10, bookmarksData.count)} of {bookmarksData.count} bookmarks
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={!hasPreviousPage}
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-md">
                  Page {page}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={!hasNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && articleToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirmOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Bookmark?</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to remove <span className="font-medium">"{articleToDelete.title}"</span> from your bookmarks? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteConfirmOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleRemoveBookmark(articleToDelete.slug)}
                    disabled={removeBookmarkMutation.isLoading}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {removeBookmarkMutation.isLoading ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
